import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Table, { TableColumn } from "../../component/table/table"
import { agent } from "../../apiClient/api"
import utilStyles from "../../asset/styles/util/util.module.scss"
import CustomizedMenu from "../../component/muiMenu/muiMenu"
import IconsColor from "../../util/iconsColor"
import Popup from "../../component/popup/popup"
import Select from "../../component/select/select"
import Button from "../../component/button/button"
import styles from "../../asset/styles/transportation/package.module.scss"
import { useSnackbar } from "../../context/snackbarContext"

export interface PackageDayTripItem {
    package_day_trip_id: string
    package_id: string
    package_name: string
    day_trip_id: string
    day_trip_name: string
}

export interface PackageTripPriceItem {
    trip_pricing_id: string
    package_id: string
    package_name: string
    seating_type_id: number
    seating_type_name: string
    price: number
    flag_active: boolean
}

export interface PackageItem {
    package_id: string
    package_name: string
    flag_active: boolean
    package_day_trip: PackageDayTripItem[]
    package_trip_price: PackageTripPriceItem[]
    updated_at_timestamp?: string
    updated_by_user_id?: number
    updated_by_user_name?: string
}

const SEATING_COLUMNS: { key: string; label: string; seatingName: string }[] = [
    { key: "4s", label: "4s", seatingName: "4 Seater" },
    { key: "7s", label: "7s", seatingName: "7 Seater" },
    { key: "16s", label: "16s", seatingName: "16 Seater" },
    { key: "29s", label: "29s", seatingName: "29 Seater" },
    { key: "35s", label: "35s", seatingName: "35 Seater" },
    { key: "45s", label: "45s", seatingName: "45 Seater" },
]

function getPriceBySeating(
    pricing: PackageTripPriceItem[] | undefined,
    seatingName: string
): number | null {
    if (!pricing?.length) return null
    const item = pricing.find((p) => p.seating_type_name === seatingName)
    return item != null ? item.price : null
}

function formatPrice(value: number | null): string {
    if (value == null) return "—"
    const n = Number(value)
    if (Number.isNaN(n)) return "—"
    return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2)
}

function formatTripsUsed(trips: PackageDayTripItem[]): React.ReactNode {
    if (!trips?.length) return "—"
    const names = trips.map((t) => t.day_trip_name)
    if (names.length <= 2) {
        return names.join(", ")
    }
    const firstTwo = names.slice(0, 2).join(", ")
    const moreCount = names.length - 2
    return (
        <>
            {firstTwo}
            <CustomizedMenu
                    buttonText={`+${moreCount} more`}
                    menuItems={names.slice(2).map((name) => ({
                        label: name,
                        onClick: () => {},
                    }))}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    showArrowIcon={false}
                />
        </>
    )
}

type ConfirmType = "status" | "delete" | null

const Package: React.FC = () => {
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const [packages, setPackages] = useState<PackageItem[]>([])
    const [selectedStatus, setSelectedStatus] = useState<string>("")
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirmType, setConfirmType] = useState<ConfirmType>(null)
    const [selectedPackage, setSelectedPackage] = useState<PackageItem | null>(null)

    const statusOptions = [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
    ]

    const filteredPackages = packages.filter((pkg) => {
        if (selectedStatus === "") return true
        if (selectedStatus === "active") return pkg.flag_active === true
        if (selectedStatus === "inactive") return pkg.flag_active === false
        return true
    })

    const handleClearFilters = () => setSelectedStatus("")
    const hasActiveFilters = !!selectedStatus

    const fetchPackageList = async () => {
        try {
            const response = await agent.PackageListAPI.get()
            if (response?.status === true && Array.isArray(response?.data)) {
                setPackages(response.data)
            } else {
                setPackages([])
            }
        } catch (error) {
            console.error("fetchPackageList Error", error)
            setPackages([])
        }
    }

    useEffect(() => {
        fetchPackageList()
    }, [])

    const openStatusConfirm = (row: PackageItem) => {
        setSelectedPackage(row)
        setConfirmType("status")
        setConfirmOpen(true)
    }

    const openDeleteConfirm = (row: PackageItem) => {
        setSelectedPackage(row)
        setConfirmType("delete")
        setConfirmOpen(true)
    }

    const closeConfirm = () => {
        setConfirmOpen(false)
        setConfirmType(null)
        setSelectedPackage(null)
    }

    const handleConfirmAction = async () => {
        const pkg = selectedPackage
        const type = confirmType
        closeConfirm()
        if (!pkg || !type) return
        if (type === "status") {
            try {
                const res = await agent.DeletePackageAPI.put({
                    package_id: pkg.package_id,
                    flag_active: !pkg.flag_active,
                    package_flag_deleted: false,
                })
                if (res?.status === false) {
                    showError(res?.error?.message ?? "Failed to update package status")
                    return
                }
                showSuccess("Package status updated successfully")
                fetchPackageList()
            } catch (e) {
                console.error("handleConfirmStatus error", e)
                showError("Failed to update package status")
            }
        } else if (type === "delete") {
            try {
                const res = await agent.DeletePackageAPI.put({
                    package_id: pkg.package_id,
                    flag_active: pkg.flag_active,
                    package_flag_deleted: true,
                })
                if (res?.status === false) {
                    showError(res?.error?.message ?? "Failed to delete package")
                    return
                }
                showSuccess("Package deleted successfully")
                fetchPackageList()
            } catch (e) {
                console.error("handleConfirmDelete error", e)
                showError("Failed to delete package")
            }
        }
    }

    const columns: TableColumn<PackageItem>[] = [
        {
            key: "package_name",
            label: "Package Name",
            width: "14%",
            align: "left",
            render: (value: string, row: PackageItem) => (
                <span
                    className={styles.packageNameLink}
                    onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/transportation/package/${row.package_id}`)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            navigate(`/transportation/package/${row.package_id}`)
                        }
                    }}
                >
                    {value}
                </span>
            ),
        },
        {
            key: "total_days",
            label: "Total Days",
            width: "8%",
            align: "center",
            render: (_: unknown, row: PackageItem) => {
                const count = row.package_day_trip?.length ?? 0
                return count === 0 ? "—" : `${count} days`
            },
        },
        {
            key: "trips_used",
            label: "Trips Used",
            width: "20%",
            align: "left",
            render: (_: unknown, row: PackageItem) => formatTripsUsed(row.package_day_trip),
        },
        ...SEATING_COLUMNS.map(({ key, label, seatingName }) => ({
            key,
            label,
            width: "6%" as const,
            align: "right" as const,
            render: (_: unknown, row: PackageItem) =>
                formatPrice(getPriceBySeating(row.package_trip_price, seatingName)),
        })),
        {
            key: "flag_active",
            label: "Status",
            width: "8%",
            align: "center",
            render: (value: boolean) => (
                <span
                    className={
                        value === true
                            ? utilStyles.statusBadgeActive
                            : value === false
                              ? utilStyles.statusBadgeInactive
                              : utilStyles.statusBadgeInactive
                    }
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "center",
            render: (_: unknown, row: PackageItem) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: row.flag_active ? "Set Status to Inactive" : "Set Status to Active",
                            onClick: () => openStatusConfirm(row),
                        },
                        {
                            label: "Delete",
                            onClick: () => openDeleteConfirm(row),
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={
                        <IconsColor
                            iconName="moreVert"
                            width="20"
                            height="20"
                            fillColor="none"
                            strokeColor="#7a7a7a"
                        />
                    }
                />
            ),
        },
    ]

    return (
        <div className={styles.packageWrapper}>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
                        <Grid size={{ xs: 12, md: 1.5 }}>
                            <div className={styles.filterGroup}>
                                <div
                                    className={`${styles.filterIconButton} ${hasActiveFilters ? styles.filterActive : ""}`}
                                    onClick={hasActiveFilters ? handleClearFilters : undefined}
                                    style={{ cursor: hasActiveFilters ? "pointer" : "default" }}
                                    title={hasActiveFilters ? "Clear Filters" : "Filters"}
                                >
                                    <IconsColor
                                        iconName={hasActiveFilters ? "clearFilterIcon" : "filterIcon"}
                                        width="20"
                                        height="20"
                                        fillColor="none"
                                        strokeColor={hasActiveFilters ? "#F44336" : "#1565C0"}
                                    />
                                </div>
                                <Select
                                    label=""
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    placeholder="Status"
                                    options={statusOptions}
                                />
                            </div>
                        </Grid>
                        <Grid size={{ xs: 12, md: 1.5 }}>
                            <Button
                                type="contained"
                                label="Add Package"
                                onClick={() => navigate("/transportation/package/new")}
                                style={{ width: "100%" }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <Table<PackageItem>
                        columns={columns}
                        data={filteredPackages}
                        keyExtractor={(row) => row.package_id}
                        emptyMessage="No packages available"
                        minWidth={1000}
                    />
                </Grid>
            </Grid>
            <Popup
                isOpen={confirmOpen}
                onClose={closeConfirm}
                title={confirmType === "status" ? "Change Package Status" : "Delete Package"}
                primaryButton={{ label: "Confirm", onClick: handleConfirmAction }}
                secondaryButton={{ label: "Cancel", onClick: closeConfirm }}
                heightFiexd={true}
            >
                {selectedPackage && confirmType === "status" ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{selectedPackage.package_name}</strong> to{" "}
                            <strong>{selectedPackage.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                ) : selectedPackage && confirmType === "delete" ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to delete <strong>{selectedPackage.package_name}</strong>?
                        </p>
                    </div>
                ) : null}
            </Popup>
        </div>
    )
}

export default Package
